import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  uuid,
  timestamp,
  numeric,
  integer,
  date,
  pgEnum,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Cross-domain imports
import { facilities } from "./schema-deals.js";
import { lenderProducts } from "./schema-lenders.js";
import { paymentFrequencyEnum } from "./schema-deals.js";
// F004 (SPRINT-30): facilityRepaymentTypeEnum moved to schema-deals.ts to avoid
// circular imports. Re-exported here for consumers that import from this module.
export { facilityRepaymentTypeEnum } from "./schema-deals.js";

// ============================================================================
// FACILITY PRICING MODULE ENUMS — SPRINT-30 F001
// ============================================================================

/** Interest-only vs P&I regime payment classification */
export const regimePaymentTypeEnum = pgEnum("regime_payment_type_enum", [
  "interest_only",
  "principal_and_interest",
]);

/** Rate lock type for the pricing regime */
export const regimeRateTypeEnum = pgEnum("regime_rate_type_enum", [
  "fixed",
  "variable",
]);

/** Whether the total rate is entered directly ('flat') or computed from base + margin ('composed') */
export const rateEntryModeEnum = pgEnum("rate_entry_mode_enum", [
  "composed",
  "flat",
]);

/** Origin of the pricing schedule record */
export const pricingSourceEnum = pgEnum("pricing_source_enum", [
  "manual",
  "product_snapshot",
  "repricing_event",
]);

/** Classification of the repricing event that triggered this schedule */
export const repricingEventTypeEnum = pgEnum("repricing_event_type_enum", [
  "initial_pricing",
  "io_to_pi_rollover",
  "scheduled_reprice",
  "ad_hoc_reprice",
]);

// ============================================================================
// TABLE: crm_x_facility_pricing_schedules — SPRINT-30 F001
// ============================================================================

export const facilityPricingSchedules = pgTable(
  "crm_x_facility_pricing_schedules",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

    // Parent FK (CASCADE on facility delete)
    facilityId: uuid("facility_id")
      .references(() => facilities.id, { onDelete: "cascade" })
      .notNull(),

    // Effective date range
    effectiveFrom: date("effective_from").notNull(),
    effectiveTo: date("effective_to"),

    // Regime metadata
    regimeMonths: integer("regime_months"),
    sequence: integer("sequence").notNull(),

    // Regime classification
    paymentType: regimePaymentTypeEnum("payment_type").notNull(),
    rateType: regimeRateTypeEnum("rate_type").notNull().default("variable"),
    entryMode: rateEntryModeEnum("entry_mode").notNull().default("composed"),

    // Rate fields are stored as decimals; scale 6 preserves 4dp percentages.
    baseRate: numeric("base_rate", { precision: 8, scale: 6 }),
    margin: numeric("margin", { precision: 8, scale: 6 }),
    totalRate: numeric("total_rate", { precision: 8, scale: 6 }),

    // Repayment fields
    repaymentFrequency: paymentFrequencyEnum("repayment_frequency").default("monthly"),
    repaymentOverride: numeric("repayment_override", { precision: 15, scale: 2 }),
    amortisationPeriodMonths: integer("amortisation_period_months"),
    // Residual/balloon owed at the end of the P&I amortisation (NULL/0 = fully
    // amortising). Its present value reduces the principal amortised by
    // pi_repayment in fn_pricing_schedule_mirror() (EDG-9093).
    balloonAmount: numeric("balloon_amount", { precision: 15, scale: 2 }),

    // Source / lineage
    source: pricingSourceEnum("source").notNull().default("manual"),
    sourceProductId: uuid("source_product_id").references(
      () => lenderProducts.id,
      { onDelete: "set null" },
    ),
    // FK to crm_x_facility_repricing_events enforced at DB level via
    // fk_schedule_source_event constraint (see migration 20260422110000).
    // Drizzle ref omitted here to avoid circular type inference:
    // facilityPricingSchedules ↔ facilityRepricingEvents
    sourceEventId: uuid("source_event_id"),

    // Audit
    notes: text("notes"),
    organisationId: uuid("organisation_id"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
    createdBy: uuid("created_by"),
    updatedBy: uuid("updated_by"),
  },
);

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const insertFacilityPricingScheduleSchema = createInsertSchema(
  facilityPricingSchedules,
  {
    facilityId: z.string().uuid(),
    effectiveFrom: z.union([z.string(), z.date()]),
    effectiveTo: z.union([z.string(), z.date()]).nullable().optional(),
    regimeMonths: z.number().int().positive().nullable().optional(),
    sequence: z.number().int().min(1),
    paymentType: z.enum(["interest_only", "principal_and_interest"]),
    rateType: z.enum(["fixed", "variable"]).optional(),
    entryMode: z.enum(["composed", "flat"]).optional(),
    baseRate: z.string().nullable().optional(),
    margin: z.string().nullable().optional(),
    totalRate: z.string().nullable().optional(),
    repaymentFrequency: z
      .enum(["weekly", "fortnightly", "monthly", "quarterly", "annually"])
      .nullable()
      .optional(),
    repaymentOverride: z.string().nullable().optional(),
    amortisationPeriodMonths: z.number().int().positive().nullable().optional(),
    balloonAmount: z
      .string()
      .refine(
        (v) => v === null || v === undefined || parseFloat(v) >= 0,
        'balloon_amount must be non-negative',
      )
      .nullable()
      .optional(),
    source: z
      .enum(["manual", "product_snapshot", "repricing_event"])
      .optional(),
    sourceProductId: z.string().uuid().nullable().optional(),
    sourceEventId: z.string().uuid().nullable().optional(),
    notes: z.string().nullable().optional(),
    organisationId: z.string().uuid().nullable().optional(),
    createdBy: z.string().uuid().nullable().optional(),
    updatedBy: z.string().uuid().nullable().optional(),
  },
);

export const selectFacilityPricingScheduleSchema = createSelectSchema(
  facilityPricingSchedules,
);

export type InsertFacilityPricingSchedule = typeof facilityPricingSchedules.$inferInsert;
export type SelectFacilityPricingSchedule = typeof facilityPricingSchedules.$inferSelect;

// ============================================================================
// TABLE: crm_x_facility_repricing_events — SPRINT-30 F002
// Append-only audit log: NO updated_at, NO updated_by
// ============================================================================

export const facilityRepricingEvents = pgTable(
  "crm_x_facility_repricing_events",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

    // Parent FK (CASCADE on facility delete)
    facilityId: uuid("facility_id")
      .references(() => facilities.id, { onDelete: "cascade" })
      .notNull(),

    // Event classification
    eventDate: date("event_date").notNull(),
    eventType: repricingEventTypeEnum("event_type").notNull(),
    triggeredBy: text("triggered_by").notNull().default("manual"),

    // Optional FK to pricing schedules (SET NULL when schedule deleted)
    supersededScheduleId: uuid("superseded_schedule_id").references(
      () => facilityPricingSchedules.id,
      { onDelete: "set null" },
    ),
    newScheduleId: uuid("new_schedule_id").references(
      () => facilityPricingSchedules.id,
      { onDelete: "set null" },
    ),

    // Optional metadata
    reason: text("reason"),
    sourceDocumentId: uuid("source_document_id"),

    // Org + audit (append-only: no updatedAt / updatedBy)
    organisationId: uuid("organisation_id"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: uuid("created_by"),
  },
);

// ============================================================================
// ZOD SCHEMAS — facilityRepricingEvents
// ============================================================================

export const insertFacilityRepricingEventSchema = createInsertSchema(
  facilityRepricingEvents,
  {
    facilityId: z.string().uuid(),
    eventDate: z.union([z.string(), z.date()]),
    eventType: z.enum([
      "initial_pricing",
      "io_to_pi_rollover",
      "scheduled_reprice",
      "ad_hoc_reprice",
    ]),
    triggeredBy: z.string().max(32).optional(),
    supersededScheduleId: z.string().uuid().nullable().optional(),
    newScheduleId: z.string().uuid().nullable().optional(),
    reason: z.string().nullable().optional(),
    sourceDocumentId: z.string().uuid().nullable().optional(),
    organisationId: z.string().uuid().nullable().optional(),
    createdBy: z.string().uuid().nullable().optional(),
  },
);

export const selectFacilityRepricingEventSchema = createSelectSchema(
  facilityRepricingEvents,
);

export type InsertFacilityRepricingEvent = typeof facilityRepricingEvents.$inferInsert;
export type SelectFacilityRepricingEvent = typeof facilityRepricingEvents.$inferSelect;
